import toastr from "toastr";
import * as logger from "./logger";
import { hasErrorResponse } from "./data";
import { scrollToId } from "./helpers";
import { attributeGroupNames } from "components/attributes/lib/constants";
import { TaskType } from "./taskTypeMap";

const defaultOptions = {
    closeButton: false,
    debug: false,
    newestOnTop: false,
    progressBar: false,
    positionClass: "toast-top-center",
    preventDuplicates: false,
    onclick: null,
    showDuration: "300",
    hideDuration: "500",
    timeOut: "2200",
    extendedTimeOut: "1000",
    showEasing: "swing",
    hideEasing: "linear",
    showMethod: "fadeIn",
    hideMethod: "fadeOut",
};

export const error = (text, options) => {
    toastr.options = options ? options : { ...defaultOptions, timeOut: "5000" };
    toastr.error(text);
};

export const success = (text) => {
    toastr.options = defaultOptions;
    toastr.success(text);
};

export const inform = (text) => {
    toastr.options = defaultOptions;
    toastr.info(text);
};

export const warning = (text) => {
    toastr.options = defaultOptions;
    toastr.warning(text);
};

export const remove = () => {
    toastr.options = defaultOptions;
    toastr.remove();
};

export const clear = () => {
    toastr.clear();
};

export const profilingScrollToAttribute = (text, attr, type) => {
    const scrollTo = () => {
        //Hardcoded since profiling group names are different from attr sort group name
        const defaultSortGroup =
            attr.sortGroup.name === "IntakeCodingSizeAttributes" ? "IntakeProfilingSizeGroup" : "IntakeProfilingIdentifyingGroup";

        const mainSection = document.getElementById(defaultSortGroup);

        const mainTitle = mainSection.firstChild;
        const mainContent = mainSection.lastChild;
        //Open coder section if not already open
        !mainContent.classList.contains("-active") && mainTitle.click();

        const parentSubGroup = attr.groups.find((group) => document.getElementById(group));
        const attributeUI = document.getElementById(attr.key) ? document.getElementById(attr.key) : document.getElementById(parentSubGroup);
        attributeUI.scrollIntoView({ behavior: "smooth" });
    };

    toastr.options = { ...defaultOptions, timeOut: "5000", onclick: scrollTo };

    toastr[type](`<div>${text} <u>Jump to Attribute</u></div>`);
};

export const scrollToAttribute = (text, attr, type, taskType) => {
    const scrollTo = () => {
        let parentGroup = attr.groups.find((group) => document.getElementById(group));
        const defaultSortGroup = attr.sortGroup ? attr.sortGroup.name : null;

        if (parentGroup || defaultSortGroup) {
            const coderSection = document.getElementById(parentGroup)
                ? document.getElementById(parentGroup).parentElement.closest(".accordion-section")
                : null;

            //Main coder section if it exists
            if (coderSection) {
                const mainTitle = coderSection.firstChild;
                const mainContent = coderSection.lastChild;
                //Open coder section if not already open
                !mainContent.classList.contains("-active") && mainTitle.click();
            }

            // Some Size attributes do not have the 'IntakeCodingSizeAttributes' group on FQA.
            if (!parentGroup && attr.groups.includes(attributeGroupNames.FQA_SIZE)) {
                parentGroup = attributeGroupNames.CODING_SIZE;
            }

            // Some Identifying attributes do not have the 'IntakeCodingIdentifyingAttributes' group on FQA. Actual FQA additional attributes have the 'FqaAdditionalAttributes' group.
            if (
                !parentGroup &&
                taskType === TaskType.FINAL_QA &&
                (defaultSortGroup === attributeGroupNames.CODING_ADDITIONAL || defaultSortGroup === attributeGroupNames.MARKETING_ATTRIBUTES)
            ) {
                parentGroup = attributeGroupNames.CODING_IDENTIFYING;
            }

            //Open parent dropdown section to reveal attribute
            const hasParent = parentGroup ? parentGroup : defaultSortGroup;

            if (!hasParent) {
                return;
            }

            const accordionTitle = document.querySelector(`#${hasParent} > .accordion-title`);
            const accordionContent = document.querySelector(`#${hasParent} > .accordion-content`);
            !accordionContent.classList.contains("-active") && accordionTitle.click();
            //Get height and offset of product header for FQA
            const productHeader = document.getElementsByClassName("product-compare")[0];
            const headerStyle = window.getComputedStyle(productHeader);
            const headerTop = parseInt(headerStyle.getPropertyValue("top"), 10);
            const headerTotalHeight = productHeader.offsetHeight + headerTop;

            const defaultGroup = parentGroup ? parentGroup : defaultSortGroup;
            const offsetValue = window.pageYOffset === 0 ? headerTotalHeight - 60 : headerTotalHeight;
            const attributeUI = document.getElementById(attr.key) ? document.getElementById(attr.key) : document.getElementById(defaultGroup);
            const offsetOverride = attributeUI.getBoundingClientRect().top + window.pageYOffset - offsetValue;

            scrollToId(attr.key, offsetOverride);
        }
    };

    toastr.options = { ...defaultOptions, timeOut: "5000", onclick: scrollTo };

    toastr[type](`<div>${text} <u>Jump to Attribute</u></div>`);
};

export const confirm = (text, config) => {
    const yesText = config && config.yesLabel ? config.yesLabel : "Yes";
    const noText = config && config.noLabel ? config.noLabel : "No";
    const confirmOnly = config && config.confirmOnly;

    toastr.remove();

    toastr.options = {
        positionClass: "toast-top-center",
        hideDuration: 500,
        timeOut: 0,
        extendedTimeOut: 0,
        tapToDismiss: false,
    };

    return new Promise((resolve) => {
        const handleKeyPress = (event) => {
            if (event.altKey && event.code === "KeyY") {
                resolveYes();
            }

            if (event.altKey && event.code === "KeyN") {
                rejectNo();
            }
        };

        const resolveYes = () => {
            document.getElementById("confirmationRevertYes").disabled = true;
            document.removeEventListener("keydown", handleKeyPress);
            resolve(true);
            remove();
        };
        const rejectNo = () => {
            document.removeEventListener("keydown", handleKeyPress);
            resolve(false);
            remove();
        };

        const buttons = confirmOnly
            ? `<div class="button-group justify-content-center">
                <button
                    type="button"
                    id="confirmationRevertYes"
                    class="button -primary"
                >
                    ${yesText}
                </button>
            </div>`
            : `<div class="button-group">
                <button
                    type="button"
                    id="confirmationRevertNo"
                    class="button"
                >
                    ${noText}
                </button>
                <button
                    type="button"
                    id="confirmationRevertYes"
                    class="button -primary -right"
                >
                    ${yesText}
                </button>
            </div>`;

        toastr.warning(`${buttons}`, text, {
            closeButton: false,
            allowHtml: true,
            onShown: function () {
                document.addEventListener("keydown", handleKeyPress);

                if (!confirmOnly) {
                    document.getElementById("confirmationRevertNo").addEventListener("click", rejectNo);
                }

                document.getElementById("confirmationRevertYes").addEventListener("click", resolveYes);
            },
        });
    });
};

export const prompt = (text, reasons, config) => {
    const yesText = config && config.yesLabel ? config.yesLabel : "Yes";
    const noText = config && config.noLabel ? config.noLabel : "No";

    toastr.options = {
        positionClass: "toast-top-center",
        hideDuration: "500",
        timeOut: 0,
        extendedTimeOut: 0,
        tapToDismiss: false,
    };

    return new Promise((resolve) => {
        const resolveYes = () => {
            document.getElementById("confirmationRevertYes").disabled = true;
            const selectMessage = document.getElementById("reject-reason") ? document.getElementById("reject-reason").value : "";
            const textMessage = document.getElementById("reject-message").value;
            const fullMessage = selectMessage && textMessage ? selectMessage + ": " + textMessage : selectMessage + textMessage;

            resolve(fullMessage);
            remove();
        };
        const rejectNo = () => {
            resolve(false);
            remove();
        };
        let html = "";
        html += "<div class='form-field'>";

        if (reasons && reasons.length > 0) {
            html += "<select id='reject-reason' class=''><option value=''>Other</option>";
            reasons.forEach((r) => {
                html += `<option value='${r}'>${r}</option>`;
            });
            html += "</select>";
        }

        html += "<textarea id='reject-message' name='reject-message'></textarea>";

        html += "</div>";
        html += `<div class='form-field'><div class='button-group'><button type='button' id='confirmationRevertNo' class='button'>${noText}</button><button type='button' id='confirmationRevertYes' class='button -primary -right'>${yesText}</button></div>`;

        toastr.warning(html, text, {
            closeButton: false,
            allowHtml: true,
            onShown: function () {
                document.getElementById("confirmationRevertNo").addEventListener("click", rejectNo);
                document.getElementById("confirmationRevertYes").addEventListener("click", resolveYes);
            },
        });
    });
};

export const errorResponse = (response, suppressError, displayError) => {
    if (response && response.errors && response.errors.length > 0) {
        const messages = response.errors.reduce((msgList, e) => {
            const fieldName = e.field ? `${e.field}: ` : "";
            const message = e.message || "";

            if (e.message || e.field) {
                msgList.push(`${fieldName}${message}`);
            }

            if (typeof e === "string") {
                msgList.push(e);
            }

            return msgList;
        }, []);
        const fields = response.errors.map((e) => {
            return e.field;
        });
        let outputMessage = messages && messages.length > 0 ? messages[0] : false;
        let outputTitle = response.message || "Error";

        if (!outputMessage) {
            if (fields && fields.length > 0) {
                const count = fields.length;
                outputTitle = "Validation Failed";
                outputMessage = `Please fix ${count} validation error${count === 1 ? "" : "s"} and try again`;
            } else {
                outputMessage = "";
            }
        }

        if (displayError ? displayError : !suppressError) {
            error(`<div>${outputTitle}</div><hr />${outputMessage}`);
            logger.error(`${outputTitle}: ${outputMessage}`);

            if (response._) {
                logger.error(response._);
            }
        }

        return true;
    } else if (response && (response.error || response.message)) {
        if (!suppressError) {
            error(`<div>${response.error || "Error"}</div><hr />${response.message}`);
            logger.error(`${response.error || "Error"}: ${response.message}`);

            if (response._) {
                logger.error(response._);
            }
        }

        return true;
    } else if (response && response.errors && typeof response.errors === "object") {
        if (response.attributeValueLogicalErrors) {
            return true;
        }

        error(`<div>${response.title}</div><hr />${response.instance || response.detail}`);
        logger.error(`${response.title}: ${response.instance || response.detail}`);

        if (response._) {
            logger.error(response._);
        }

        return true;
    } else if (hasErrorResponse(response)) {
        // this handler might be caught by other endpoint besides POST tasking/tasks/statechanges/multiple - ensure this config is  ok
        if (!displayError) {
            return;
        }

        response.forEach((res) => {
            error(`<div>${"Error"}</div><hr />${res.error.message}`, {
                ...defaultOptions,
                timeOut: 0,
                extendedTimeOut: 0,
                closeButton: true,
                tapToDismiss: false,
            });
            logger.error(`${response.error || "Error"}: ${res.error.message}`);
        });

        return true;
    }

    return false;
};
